import { useState } from 'react';
import { usePageSize } from './PageSizeContext';

/** Everything the shared table footer needs from a pager — the hook's return
 *  minus the page slice itself. */
export interface IBreakdownPager {
  total: number;
  page: number;
  pageCount: number;
  /** 1-based bounds of the visible slice, for the "11–20 of 43" label. */
  rangeStart: number;
  rangeEnd: number;
  hasPages: boolean;
  goPrev: () => void;
  goNext: () => void;
}

/**
 * Client-side pagination for a breakdown table. Given the already-sorted rows,
 * it returns the current page slice plus everything a pager row needs. The
 * page size comes from the shared PageSizeContext, so every breakdown table
 * pages at the same, user-chosen size.
 *
 * The page snaps back to the first when `resetKey` changes (panels pass their
 * sort state — after a re-sort the user wants the top of the new order, not
 * frame 3 of it) or when the page size changes. The key must be a primitive;
 * comparing the rows array itself would re-render forever whenever a caller
 * builds the array inline. Row-set shrinkage (timeframe change) is handled by
 * clamping instead — and the clamp is committed to state, so a later regrow
 * of the row set stays on the clamped page rather than snapping back to the
 * deep page the user was on before the shrink.
 */
export function usePagination<T>(rows: T[], resetKey?: string): { visible: T[] } & IBreakdownPager {
  const { pageSize } = usePageSize();
  const key = `${resetKey ?? ''}|${pageSize}`;
  const [page, setPage] = useState(0);
  const [prevKey, setPrevKey] = useState(key);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  if (prevKey !== key) {
    // Render-phase reset (React's derive-state-from-props pattern) — avoids a
    // one-frame flash of a stale page against the new order.
    setPrevKey(key);
    setPage(0);
  } else if (page > pageCount - 1) {
    // Render-phase commit of the clamp (same pattern as the reset above); the
    // `else` keeps a simultaneous key change winning with its reset to 0.
    setPage(pageCount - 1);
  }
  const current = Math.min(page, pageCount - 1);
  const rangeStart = current * pageSize;
  const visible = rows.slice(rangeStart, rangeStart + pageSize);

  return {
    visible,
    total: rows.length,
    page: current,
    pageCount,
    rangeStart: rows.length === 0 ? 0 : rangeStart + 1,
    rangeEnd: rangeStart + visible.length,
    hasPages: pageCount > 1,
    goPrev: () => setPage(Math.max(0, current - 1)),
    goNext: () => setPage(Math.min(pageCount - 1, current + 1)),
  };
}
